import React from 'react';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDAvatar from 'components/MDAvatar';

import { roleLabel, statusChip } from '../utils/staffFormatters';

function initials(name = '') {
  const p = name.trim().split(' ').filter(Boolean);
  const a = (p[0]?.[0] || '').toUpperCase();
  const b = (p[1]?.[0] || '').toUpperCase();
  return a + b || 'U';
}

function safeText(x) {
  return (x ?? '').toString().trim();
}

export function StaffMobileCardItem({ staff, onOpenMenu, onDetail, onEdit }) {
  const theme = useMuiTheme();
  const st = statusChip(staff?.active);

  const name = safeText(staff?.fullName) || '-';
  const role = roleLabel(staff?.staffRole ?? staff?.role) || '-';
  const phone = safeText(staff?.phone) || '—';
  const email = safeText(staff?.email) || '—';

  // ✅ status chip: yeşile boyamadan, “premium” soft
  const chipSx = staff?.active
    ? {
        background: alpha(theme.palette.success.main, 0.12),
        color: theme.palette.success.main,
        border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
      }
    : {
        background: alpha(theme.palette.grey[500], 0.1),
        color: theme.palette.text.secondary,
        border: `1px solid ${alpha(theme.palette.grey[500], 0.22)}`,
      };

  return (
    <Card
      sx={{
        borderRadius: '18px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[2],
        overflow: 'hidden',
        background: theme.palette.background.paper,
      }}
    >
      <MDBox sx={{ p: 1 }}>
        {/* header row */}
        <MDBox sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <MDAvatar
            bgColor="dark"
            size="md"
            sx={{
              width: 38,
              height: 38,
              borderRadius: '999px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              fontWeight: 900,
            }}
          >
            {initials(name)}
          </MDAvatar>

          <MDBox sx={{ minWidth: 0, flex: 1 }}>
            <MDBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  sx={{
                    display: 'block',
                    fontSize: '0.98rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {name}
                </MDTypography>

                <MDTypography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    opacity: 0.95,
                  }}
                >
                  {role}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.9, flexShrink: 0 }}>
                <Chip
                  size="small"
                  icon={<Icon sx={{ fontSize: 16, opacity: 0.9 }}>{st.icon}</Icon>}
                  label={st.label}
                  sx={{
                    height: 26,
                    borderRadius: '999px',
                    fontWeight: 800,
                    ...chipSx,
                  }}
                />

                <Tooltip title="İşlemler">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenMenu?.(e, staff);
                    }}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '12px',
                      border: `1px solid ${theme.palette.divider}`,
                      background: theme.palette.background.paper,
                      '&:hover': { background: alpha(theme.palette.grey[500], 0.06) },
                    }}
                  >
                    <Icon fontSize="small">more_vert</Icon>
                  </IconButton>
                </Tooltip>
              </MDBox>
            </MDBox>

            {/* details */}
            <MDBox sx={{ mt: 0.5, display: 'grid', gap: 0.2 }}>
              <MDTypography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <span style={{ opacity: 0.7 }}>Tel:</span> {phone}
              </MDTypography>

              <MDTypography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <span style={{ opacity: 0.7 }}>E-posta:</span> {email}
              </MDTypography>
            </MDBox>

            {/* actions */}
            <MDBox sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
              <MDBox
                component="button"
                onClick={() => onDetail?.(staff)}
                sx={{
                  flex: 1,
                  height: 38,
                  borderRadius: '12px',
                  border: `1px solid ${theme.palette.divider}`,
                  background: theme.palette.background.paper,
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                  '&:hover': { background: alpha(theme.palette.grey[500], 0.06) },
                }}
              >
                Detay
              </MDBox>

              <MDBox
                component="button"
                onClick={() => onEdit?.(staff)}
                sx={{
                  flex: 1,
                  height: 38,
                  borderRadius: '12px',
                  border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                  background: alpha(theme.palette.info.main, 0.1),
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontWeight: 900,
                  color: theme.palette.info.main,
                  '&:hover': { background: alpha(theme.palette.info.main, 0.14) },
                }}
              >
                Düzenle
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}
